/**
 * Пара кнопок «Сохранить» и «Отменить» для раздела с черновиком (§6).
 *
 * Один компонент на все разделы. Раньше кнопка сохранения была скопирована
 * в каждый экран отдельно, и любая правка — размер, подпись, поведение на
 * телефоне — требовала помнить про семь мест. Отмены не было вовсе: передумав,
 * правки приходилось откатывать руками по одной либо уходить со страницы
 * и отвечать на вопрос о несохранённом.
 *
 * Обе кнопки появляются на телефоне, только когда есть что сохранять: там они
 * занимают строку, а строка — это ещё одна видимая запись. На широком экране
 * они стоят всегда и просто гаснут: место есть, а исчезающие кнопки сбивают
 * с толку.
 *
 * Перед откатом СПРАШИВАЕМ. Отменённые правки вернуть нечем, а кнопка стоит
 * вплотную к «Сохранить» — промах стоил бы всей работы с разделом.
 */

import { useState, type ReactNode } from 'react';
import { Check, X } from 'lucide-react';
import { ConfirmDialog } from './ConfirmDialog.js';

export function SaveDiscard({
  dirty,
  saving,
  onSave,
  onDiscard,
  /** Что вернётся к сохранённому виду: «Заказы», «Врачи», «Настройки». */
  what,
  /** Значок сохранения — в «Настройках» дискета, в остальных галочка. */
  icon,
}: {
  dirty: boolean;
  saving: boolean;
  onSave: () => void;
  onDiscard: () => void;
  what: string;
  icon?: ReactNode;
}) {
  const [confirm, setConfirm] = useState(false);

  const shape =
    /* Рамка прозрачная у сплошной кнопки — иначе она на две точки ниже
       соседней «Отменить» с рамкой (как в `Button`). */
    'items-center gap-1.5 rounded-lg border border-transparent px-2.5 py-1.5 text-[13px] font-medium disabled:opacity-40 md:inline-flex md:px-3.5 md:py-2 md:text-sm';
  const visible = dirty ? 'inline-flex' : 'hidden';

  return (
    <>
      <button
        onClick={onSave}
        disabled={saving || !dirty}
        className={`${shape} text-white ${visible}`}
        style={{ background: 'var(--otc-accent-dark)' }}
      >
        {icon ?? <Check size={15} />}
        <span className="whitespace-nowrap">{saving ? 'Сохранение…' : 'Сохранить'}</span>
      </button>

      {/* Коралловый контуром, не заливкой: действие разрушительное, но
          не опасное, и кричать ему незачем. */}
      <button
        onClick={() => setConfirm(true)}
        disabled={saving || !dirty}
        className={`${shape} border ${visible}`}
        style={{ borderColor: 'var(--otc-coral)', color: 'var(--otc-coral)' }}
      >
        <X size={15} />
        <span className="whitespace-nowrap">Отменить</span>
      </button>

      {confirm && (
        <ConfirmDialog
          title="Отменить несохранённые изменения?"
          description={`${what} вернутся к тому виду, в каком они сохранены на сервере.`}
          details="Отменённые правки не восстанавливаются."
          confirmLabel="Да, отменить"
          onConfirm={() => {
            onDiscard();
            setConfirm(false);
          }}
          onCancel={() => setConfirm(false)}
        />
      )}
    </>
  );
}
